import { generatePath } from 'react-router';

import { Button, Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FieldLabel } from 'Components/FieldLabel/FieldLabel';
import { TagListing } from 'Components/Tags/TagListing/TagListing';
import { Text } from 'Components/Text/Text';
import { UCRM_FOLDERS_LISTING, UCRM_ISSUES_EXCEPTIONS } from 'Config/PathTemplates';
import { UCRM_BUSINESS_MANAGER, UCRM_USE_CASE_DELEGATES, UCRM_USE_CASE_DESCRIPTION, UCRM_USE_CASE_MANAGER } from 'Config/Tooltips';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { SEARCH_PARAM_FOLDER_PRESELECTED_ORGANIZATION_ID, SEARCH_PARAM_ISSUES_EXCEPTIONS_ORGANIZATION_ID } from 'Helpers/URLBuilder/URLBuilder';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { countOpenExceptions } from 'Models/Exceptions';
import { FolderVersion } from 'Models/Folders';
import { countOpenIssues } from 'Models/Issues';
import { BusinessResponseWithUseCases, BusinessWithUseCasesForNonUcrmUser, UseCase, UseCaseForNonUcrmUser } from 'Models/Ucrm';
import { UserResponse } from 'Models/User';

import styles from './BusinessAndUseCaseTable.module.css';

export interface BusinessAndUseCaseTableExpandedInformationProps {
    business: BusinessResponseWithUseCases | BusinessWithUseCasesForNonUcrmUser;
    folders: FolderVersion[];
    onDisplayUsers: (userIds: string[], header: string) => void;
    userHasFullUcrmAccess: boolean;

    useCase?: UseCase | UseCaseForNonUcrmUser;
}

export const BusinessAndUseCaseTableExpandedInformation = (props: BusinessAndUseCaseTableExpandedInformationProps) => {
    const cachedData = useCachedData();

    const { business, useCase, userHasFullUcrmAccess } = props;

    return (
        <div className={styles.collapsedInformationContainer}>
            <div className={styles.collapsedBusinessInformation}>
                <Text variant="Header3">{props.business.name}</Text>
                <div className={styles.detailsContainer}>
                    <div className={styles.detailsGridSection}>
                        <div className={styles.detailsGridColumn}>
                            <div className={styles.labelAndValueContainerInsideOfGrid}>
                                <FieldLabel fieldName="Manager" tooltipText={UCRM_BUSINESS_MANAGER} />
                                <Text>{getUserNameFromSubject(business.vendor_manager_user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) ?? ''}</Text>
                            </div>
                            {userHasFullUcrmAccess && (
                                <div className={styles.labelAndValueContainerInsideOfGrid}>
                                    <FieldLabel fieldName="Issues" />
                                    <Link size="sm" to={{ pathname: UCRM_ISSUES_EXCEPTIONS, search: `${SEARCH_PARAM_ISSUES_EXCEPTIONS_ORGANIZATION_ID}=${business.id}`, hash: '#issues' }}>
                                        {getOpenIssuesLabel(business as BusinessResponseWithUseCases)}
                                    </Link>
                                </div>
                            )}
                        </div>
                        <div className={styles.detailsGridColumn}>
                            <div className={styles.labelAndValueContainerInsideOfGrid}>
                                <FieldLabel fieldName="Created" />
                                <Text>{getCreatedString(cachedData.users, business.created_time, business.created_by)}</Text>
                            </div>
                            {userHasFullUcrmAccess && (
                                <div className={styles.labelAndValueContainerInsideOfGrid}>
                                    <FieldLabel fieldName="Exceptions" />
                                    <Link size="sm" to={{ pathname: UCRM_ISSUES_EXCEPTIONS, search: `${SEARCH_PARAM_ISSUES_EXCEPTIONS_ORGANIZATION_ID}=${business.id}`, hash: '#exceptions' }}>
                                        {getOpenExceptionsLabel(business as BusinessResponseWithUseCases)}
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                    {userHasFullUcrmAccess && (
                        <div className={styles.labelAndValueContainerInsideOfGrid}>
                            <FieldLabel fieldName="Folders" />
                            <Link size="sm" to={{ pathname: generatePath(UCRM_FOLDERS_LISTING), search: `${SEARCH_PARAM_FOLDER_PRESELECTED_ORGANIZATION_ID}=${business.id}` }}>
                                {props.folders.length === 1 ? '1 folder' : `${props.folders.length} folders`}
                            </Link>
                        </div>
                    )}
                    <div className={styles.labelAndValueContainerOutsideOfGrid}>
                        <FieldLabel fieldName="Tags" />
                        {business.tags.length > 0 ? <TagListing tags={cachedData.convertTagIdsToDisplayableTags(business.tags)} showAll /> : <Text>-</Text>}
                    </div>
                </div>
            </div>
            {useCase && (
                <>
                    <div>
                        <div className={styles.collapsedInformationSeparator} />
                    </div>
                    <div className={styles.collapsedUseCaseInformation}>
                        <Text variant="Header3">{useCase.name}</Text>
                        <div className={styles.detailsContainer}>
                            <div className={styles.detailsGridSection}>
                                <div className={styles.detailsGridColumn}>
                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                        <FieldLabel fieldName="Manager" tooltipText={UCRM_USE_CASE_MANAGER} />
                                        <Text>{getUserNameFromSubject(useCase.vendor_service_manager_user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) ?? ''}</Text>
                                    </div>
                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                        <FieldLabel fieldName="Last Assessed" />
                                        <Text>{useCase.last_assessed ? iso8601ToUsDateShort(useCase.last_assessed) : '-'}</Text>
                                    </div>
                                </div>
                                <div className={styles.detailsGridColumn}>
                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                        <FieldLabel fieldName="Created" />
                                        <Text>{getCreatedString(cachedData.users, useCase.created_time, useCase.created_by)}</Text>
                                    </div>
                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                        <FieldLabel fieldName="Delegates" tooltipText={UCRM_USE_CASE_DELEGATES} />
                                        <Button variant="linkText" size="sm" onClick={() => props.onDisplayUsers(useCase.delegates, 'Delegates')}>{`${useCase.delegates.length} ${useCase.delegates.length === 1 ? 'delegate' : 'delegates'}`}</Button>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.labelAndValueContainerOutsideOfGrid}>
                                <FieldLabel fieldName="Inherent Risk Questionnaire Due Date" />
                                <Text>{useCase.assessment_workflow_setup.irq_target_completion_date ? iso8601ToUsDateShort(useCase.assessment_workflow_setup.irq_target_completion_date) : '-'}</Text>
                            </div>
                            <div className={styles.labelAndValueContainerOutsideOfGrid}>
                                <FieldLabel fieldName="Description" tooltipText={UCRM_USE_CASE_DESCRIPTION} />
                                <Text>{useCase.description}</Text>
                            </div>
                            <div className={styles.labelAndValueContainerOutsideOfGrid}>
                                <FieldLabel fieldName="Tags" />
                                {useCase.tags.length > 0 ? <TagListing tags={cachedData.convertTagIdsToDisplayableTags(useCase.tags)} showAll /> : <Text>-</Text>}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const getCreatedString = (users: UserResponse[], created_time: string, createdByUserId?: string) => (created_time && createdByUserId ? `${iso8601ToUsDateShort(created_time)} by ${getUserNameFromSubject(createdByUserId, users, UserNameFormat.FIRST_SPACE_LAST)}` : iso8601ToUsDateShort(created_time));

// TODO: These will potentially be repeated multiple times per business. Consider memoizing a Map.
const getOpenIssuesLabel = (business: BusinessResponseWithUseCases) => {
    const openIssues = countOpenIssues(business.issues);
    return openIssues === 1 ? '1 open issue' : `${openIssues} open issues`;
};
const getOpenExceptionsLabel = (business: BusinessResponseWithUseCases) => {
    const openExceptions = countOpenExceptions(business.exceptions);
    return openExceptions === 1 ? '1 open exception' : `${openExceptions} open exceptions`;
};
